import { hasSharedAccessGuard } from '@booking-portal/router/guards/has-shared-access';
import { isAuthenticatedGuard } from '@booking-portal/router/guards/is-authenticated-guard';
import { isNotAuthenticatedGuard } from '@booking-portal/router/guards/is-not-authenticated-guard';
import { requiresLanguageGuard } from '@booking-portal/router/guards/requires-language-guard';
import { guardNames } from '@booking-portal/router/router-constants';
import { useAuthStore } from '@booking-portal/stores/auth-store';
import { applyGuard } from '@booking-portal/utils/guard-utils';
import type { Router } from 'vue-router';

export function setupGuards(router: Router) {
  /**
   * Checks the session state before the first navigation attempt. It delays the first navigation until done.
   * Because if this the app starts with a clear session state and the guards do the rest.
   * The session check needs to be the first guard!
   *
   * Must always return true to allow the navigation.
   */
  let checked = false;

  router.beforeEach(async () => {
    // Only perform this check once on application start. This is what the checked variable is for.
    if (!checked) {
      checked = true;

      const authState = useAuthStore();
      return await authState
        .initializeSession()
        .then(() => {
          return true;
        })
        .catch(() => true);
    }

    return true;
  });

  // Single source of truth: adding a guard here both registers it and makes it a known name.
  const guards = {
    [guardNames.authenticatedGuard]: isAuthenticatedGuard,
    [guardNames.notAuthenticatedGuard]: isNotAuthenticatedGuard,
    [guardNames.hasSharedAccess]: hasSharedAccessGuard,
    [guardNames.requiresLanguage]: requiresLanguageGuard,
  };

  const knownGuards = new Set(Object.keys(guards));

  // Validate middleware names before any guard runs so unknown names fail loudly.
  router.beforeEach((to) => {
    const middleware = to.meta?.middleware ?? [];
    for (const name of middleware) {
      if (!knownGuards.has(name)) {
        const msg = `Unknown middleware "${name}" on route "${String(to.name)}"`;
        if (import.meta.env.DEV) {
          throw new Error(msg);
        } else {
          console.error(msg);
        }
      }
    }
  });

  for (const [name, guard] of Object.entries(guards)) {
    router.beforeEach(applyGuard(name, guard));
  }
}
